// src/components/PrintPDFButton.jsx
import React, { useState } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PdfDocument from "../helpers/PdfDocument";
import {
  Button,
  CircularProgress,
  LinearProgress,
  Box,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import imageCompression from "browser-image-compression";

const PrintPDFButton = ({
  formStructure,
  responseData,
  signatures,
  companyName,
  responseStatus,
  auditScore,
}) => {
  const [compressedData, setCompressedData] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [imageProgress, setImageProgress] = useState({ done: 0, total: 0 });

  const handleGenerate = async () => {
    setPreparing(true);
    // clone responseData so we can swap in compressed images
    const out = { ...responseData };
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };

    // Fetch a remote image and return a compressed base64 data URI. react-pdf
    // silently drops large remote images at render time, so we embed them here.
    const compressUri = async (uri) => {
      try {
        const blob = await fetch(uri).then((r) => r.blob());
        const file = new File([blob], "img.jpg", { type: blob.type });
        const compressedFile = await imageCompression(file, options);
        return await imageCompression.getDataUrlFromFile(compressedFile);
      } catch {
        return uri; // fallback if compression fails
      }
    };

    // Flatten all modules — image fields are usually nested inside sections.
    const allModules = [];
    const walk = (mods = []) => {
      mods.forEach((m) => {
        allModules.push(m);
        if (Array.isArray(m.children)) walk(m.children);
        if (Array.isArray(m.questions)) walk(m.questions);
      });
    };
    walk(formStructure.modules || []);

    // Count every image up front so the progress bar can be determinate.
    const imageCountFor = (mod) => {
      if (mod.type === "image_upload" || mod.type === "photo_gallery") {
        return (responseData[mod.id] || []).length;
      }
      if (mod.type === "inspection_item") {
        const media = responseData[mod.id]?.media;
        return Array.isArray(media) ? media.length : 0;
      }
      return 0;
    };
    const total = allModules.reduce((n, mod) => n + imageCountFor(mod), 0);
    setImageProgress({ done: 0, total });

    // Bump the counter as each image finishes (fetch + compress = one unit).
    let done = 0;
    const track = (uri) =>
      compressUri(uri).then((result) => {
        done += 1;
        setImageProgress({ done, total });
        return result;
      });

    // Compress every image-bearing field so they embed reliably in the PDF.
    for (const mod of allModules) {
      const key = mod.id;
      if (mod.type === "image_upload" || mod.type === "photo_gallery") {
        const arr = responseData[key] || [];
        out[key] = await Promise.all(arr.map(track));
      } else if (mod.type === "inspection_item") {
        const entry = responseData[key];
        const media = entry?.media;
        if (Array.isArray(media) && media.length) {
          out[key] = { ...entry, media: await Promise.all(media.map(track)) };
        }
      }
    }

    setCompressedData(out);
    setPreparing(false);
    setGenerated(true);
  };

  // ── Phase 1a: idle — plain button, no layout change ───────────────────────
  if (!generated && !preparing) {
    return (
      <Button variant="contained" onClick={handleGenerate}>
        Generate PDF
      </Button>
    );
  }

  // ── Phase 1b: preparing — download + resize each photo (the slow part) ─────
  if (!generated) {
    const { done, total } = imageProgress;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

    return (
      <Box sx={{ width: 300, maxWidth: "100%" }}>
        <Button
          variant="contained"
          fullWidth
          disabled
          startIcon={<CircularProgress size={20} color="inherit" />}
        >
          Preparing PDF…
        </Button>
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress
            variant={total > 0 ? "determinate" : "indeterminate"}
            value={pct}
            sx={{ height: 6, borderRadius: 1 }}
          />
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.75, fontWeight: 600 }}
          >
            {total > 0 ? `Optimising photos — ${done} of ${total}` : "Preparing…"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
          >
            Each photo is downloaded and resized so it embeds reliably in the
            PDF. Reports with many or high-resolution photos take longer.
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Phase 2: react-pdf assembles the document in the browser ──────────────
  return (
    <PDFDownloadLink
      document={
        <PdfDocument
          formStructure={formStructure}
          responseData={compressedData}
          signatures={signatures}
          companyName={companyName}
          responseStatus={responseStatus}
          auditScore={auditScore}
        />
      }
      fileName="form-response.pdf"
    >
      {({ loading }) =>
        loading ? (
          <Box sx={{ width: 300, maxWidth: "100%" }}>
            <Button
              variant="contained"
              fullWidth
              disabled
              startIcon={<CircularProgress size={20} color="inherit" />}
            >
              Building PDF…
            </Button>
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress sx={{ height: 6, borderRadius: 1 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75, lineHeight: 1.4 }}
              >
                Assembling pages and embedding
                {imageProgress.total > 0 ? ` ${imageProgress.total} ` : " "}
                images in your browser. This can take a few seconds for large
                reports.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Download PDF
          </Button>
        )
      }
    </PDFDownloadLink>
  );
};

export default PrintPDFButton;
